import './App.css';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Pricing from './pages/Pricing';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import GoalPlanning from './pages/GoalPlanning';
import RiskAnalysis from './pages/RiskAnalysis';
import ComparisonTool from './pages/ComparisonTool';
import CryptoMarket from './pages/CryptoMarket';
import FiatMarket from './pages/FiatMarket';
import ModeSelectorPage from './pages/ModeSelectorPage';
import CryptoHome from './pages/CryptoHome';
import FiatHome from './pages/FiatHome';
import CryptoLayout from './components/CryptoLayout';
import FiatLayout from './components/FiatLayout';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import Support from './pages/Support';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { HistoryProvider } from './context/HistoryContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { ReminderProvider } from './context/ReminderContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ExchangeRateProvider } from './context/ExchangeRateContext';
import { NotificationProvider } from './context/NotificationContext';
import { ModeProvider, useMode } from './context/ModeContext';
import { CryptoProvider } from './context/CryptoContext';
import { FiatMarketProvider } from './context/FiatMarketContext';
import NotificationCenter from './components/NotificationCenter';

// Protected Route Component
function ProtectedRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-page">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

// Root route - guests go to demo, logged-in users go to mode selector
function RootRoute() {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-page">
                <div className="spinner"></div>
            </div>
        );
    }

    return <Navigate to={isAuthenticated ? "/select-mode" : "/demo"} replace />;
}

// Public Route - redirects to mode selector if already logged in
function PublicRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-page">
                <div className="spinner"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/select-mode" replace />;
    }

    return children;
}

// Mode Required Route - redirects to mode selector if no mode selected
function ModeRequiredRoute({ children }) {
    const { hasSelectedMode } = useMode();

    if (!hasSelectedMode) {
        return <Navigate to="/select-mode" replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            <Route path="/register" element={
                <PublicRoute>
                    <Register />
                </PublicRoute>
            } />
            <Route path="/forgot-password" element={
                <PublicRoute>
                    <ForgotPassword />
                </PublicRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Semi-Public Routes (accessible without login) */}
            <Route path="/demo" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/faq" element={<FAQ />} />

            {/* Mode Selection */}
            <Route path="/select-mode" element={
                <ProtectedRoute>
                    <ModeSelectorPage />
                </ProtectedRoute>
            } />

            {/* Crypto Routes */}
            <Route path="/crypto" element={
                <ProtectedRoute>
                    <ModeRequiredRoute>
                        <CryptoLayout />
                    </ModeRequiredRoute>
                </ProtectedRoute>
            }>
                <Route index element={<CryptoHome />} />
                <Route path="market" element={<CryptoMarket />} />
                <Route path="goals" element={<GoalPlanning mode="crypto" />} />
                <Route path="risk" element={<RiskAnalysis mode="crypto" />} />
                <Route path="compare" element={<ComparisonTool mode="crypto" />} />
            </Route>

            {/* Fiat Routes */}
            <Route path="/fiat" element={
                <ProtectedRoute>
                    <ModeRequiredRoute>
                        <FiatLayout />
                    </ModeRequiredRoute>
                </ProtectedRoute>
            }>
                <Route index element={<FiatHome />} />
                <Route path="market" element={<FiatMarket />} />
                <Route path="goals" element={<GoalPlanning mode="fiat" />} />
                <Route path="risk" element={<RiskAnalysis mode="fiat" />} />
                <Route path="compare" element={<ComparisonTool mode="fiat" />} />
            </Route>

            {/* Shared Protected Routes */}
            <Route path="/support" element={
                <ProtectedRoute>
                    <Support />
                </ProtectedRoute>
            } />
            <Route path="/profile" element={
                <ProtectedRoute>
                    <Profile />
                </ProtectedRoute>
            } />
            <Route path="/settings" element={
                <ProtectedRoute>
                    <Settings />
                </ProtectedRoute>
            } />

            {/* Root route */}
            <Route path="/" element={<RootRoute />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <LanguageProvider>
                <AuthProvider>
                    <SocketProvider>
                    <ModeProvider>
                        <CryptoProvider>
                            <FiatMarketProvider>
                            <ExchangeRateProvider>
                                <NotificationProvider>
                                <HistoryProvider>
                                    <FavoritesProvider>
                                        <ReminderProvider>
                                            <BrowserRouter>
                                                <NotificationCenter />
                                                <AppRoutes />
                                            </BrowserRouter>
                                        </ReminderProvider>
                                    </FavoritesProvider>
                                </HistoryProvider>
                                </NotificationProvider>
                            </ExchangeRateProvider>
                            </FiatMarketProvider>
                        </CryptoProvider>
                    </ModeProvider>
                    </SocketProvider>
                </AuthProvider>
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;
